
import Vue, { PropType } from 'vue';
import { DatatableConfig, DatatableMixin } from '@vedicium/metronic-vue';
import { DatatableUtil } from '@vedicium/vue-core';
import { Mutation, MutationsService } from '@vedicium/object-mutations';

import {
  SunbedModelEntity,
  SunbedModelTypeDto,
  SunbedModelTypeEntity,
  SunbedModelTypeOptionMode,
} from '../../../../../../../services/sunbeds';
import { Core } from '../../../../../../../services/core';
import { OptionPartEntity, OptionPartsStore } from '../../../../../../../services/parts';

export default Vue.extend({
  name: 'configurationViewTypesOptionsCompatibilityForm',
  mixins: [DatatableMixin],
  props: {
    model: {
      type: Object as PropType<SunbedModelEntity>,
    },
    document: {
      type: Object as PropType<SunbedModelTypeDto>,
    },
  },
  data() {
    return {
      datatable: {
        url: OptionPartsStore.url,
        fields: [
          {
            name: 'description',
            title: 'Description',
            titleClass: 'w-200px',
            dataClass: 'w-200px',
          },
          {
            name: 'article_number',
            title: 'Article number',
            titleClass: 'w-200px',
            dataClass: 'w-200px',
          },
          {
            name: 'mode',
            title: 'Mode',
            titleClass: 'w-150px',
            dataClass: 'w-150px',
          },
          {
            name: 'only_together',
            title: 'Only together',
            titleClass: 'w-50px',
            dataClass: 'w-50px',
          },
          {
            name: 'not_together_with',
            title: 'Not together',
            titleClass: 'w-50px',
            dataClass: 'w-50px',
          },
        ],

        fetch: DatatableUtil.fetch(Core),
        httpOptions: {
          params: {
            'filter[models]': this.model._meta.guid,
          },
        },
      } as DatatableConfig,

      metadata: {
        modes: Object.values(SunbedModelTypeOptionMode),
      },
    };
  },

  methods: {
    getOptionCompatibilityItem(
      entity: OptionPartEntity,
    ): SunbedModelTypeEntity['parts']['options'][number] | undefined {
      return this.document.parts.options.find((row) => row.guid === entity._meta.guid);
    },
    getMode(entity: OptionPartEntity): SunbedModelTypeOptionMode {
      return (
        this.getOptionCompatibilityItem(entity)?.mode || SunbedModelTypeOptionMode.NOT_AVAILABLE
      );
    },
    getTogetherWith(entity: OptionPartEntity): number | undefined {
      return this.getOptionCompatibilityItem(entity)?.together_with;
    },
    getNotTogetherWith(entity: OptionPartEntity): number | undefined {
      return this.getOptionCompatibilityItem(entity)?.not_together_with;
    },

    isAvailable(entity: OptionPartEntity): boolean {
      return this.getOptionCompatibilityItem(entity) !== undefined;
    },
    isTogetherWithAvailable(entity: OptionPartEntity): boolean {
      return [SunbedModelTypeOptionMode.AVAILABLE].includes(this.getMode(entity));
    },
    isNotTogetherWithAvailable(entity: OptionPartEntity): boolean {
      return [
        SunbedModelTypeOptionMode.AVAILABLE,
        SunbedModelTypeOptionMode.PACKAGE_ONLY,
        SunbedModelTypeOptionMode.SELECTED_BY_DEFAULT,
      ].includes(this.getMode(entity));
    },

    onModeChange(entity: OptionPartEntity, value: SunbedModelTypeOptionMode): void {
      const mutations: Array<Mutation<SunbedModelTypeDto>> = [];

      switch (value) {
        case SunbedModelTypeOptionMode.AVAILABLE:
        case SunbedModelTypeOptionMode.PACKAGE_ONLY:
        case SunbedModelTypeOptionMode.SELECTED_BY_DEFAULT:
        case SunbedModelTypeOptionMode.INCLUDED_BY_DEFAULT: {
          if (this.getOptionCompatibilityItem(entity) !== undefined) {
            mutations.push({
              action: 'array:objects:set_field',
              field: 'parts.options',
              query: {
                filter: 'eq',
                field: 'guid',
                value: entity._meta.guid,
              },
              mutation: {
                field: 'mode',
                value: value,
              },
            });
          } else {
            mutations.push({
              action: 'array:push_unique',
              field: 'parts.options',
              value: {
                guid: entity._meta.guid,
                mode: value,
                together_with: undefined,
                not_together_with: undefined,
              },
            });
          }

          break;
        }

        case SunbedModelTypeOptionMode.NOT_AVAILABLE:
        default: {
          mutations.push({
            action: 'array:objects:remove',
            field: 'parts.options',
            query: {
              filter: 'eq',
              field: 'guid',
              value: entity._meta.guid,
            },
          });

          break;
        }
      }

      // Remove 'together with' & 'not together with' fields when included by default is selected
      if (value === SunbedModelTypeOptionMode.INCLUDED_BY_DEFAULT) {
        mutations.push(
          {
            action: 'array:objects:set_field',
            field: 'parts.options',
            query: {
              filter: 'eq',
              field: 'guid',
              value: entity._meta.guid,
            },
            mutation: {
              field: 'together_with',
              value: undefined,
            },
          },
          {
            action: 'array:objects:set_field',
            field: 'parts.options',
            query: {
              filter: 'eq',
              field: 'guid',
              value: entity._meta.guid,
            },
            mutation: {
              field: 'not_together_with',
              value: undefined,
            },
          },
        );
      }

      const document = MutationsService.apply(this.document, mutations);
      this.$set(this.document.parts, 'options', document.parts.options);
    },
    onTogetherWithChange(entity: OptionPartEntity, value: string | undefined): void {
      const document = MutationsService.apply(this.document, [
        {
          action: 'array:objects:set_field',
          field: 'parts.options',
          query: { filter: 'eq', field: 'guid', value: entity._meta.guid },
          mutation: {
            field: 'together_with',
            value: Number(value) || undefined,
          },
        },
      ]);

      this.$set(this.document.parts, 'options', document.parts.options);
    },
    onNotTogetherWithChange(entity: OptionPartEntity, value: string | undefined): void {
      const document = MutationsService.apply(this.document, [
        {
          action: 'array:objects:set_field',
          field: 'parts.options',
          query: { filter: 'eq', field: 'guid', value: entity._meta.guid },
          mutation: {
            field: 'not_together_with',
            value: Number(value) || undefined,
          },
        },
      ]);

      this.$set(this.document.parts, 'options', document.parts.options);
    },
  },
});
